
import { supabase } from '@/integrations/supabase/client';
import commentsMetricsService from './metrics/commentsMetricsService';
import likesMetricsService from './metrics/likesMetricsService';
import sharesMetricsService from './metrics/sharesMetricsService';
import { ContentType } from './comments/types';

export type SocialMetrics = {
  commentsCount: number;
  likesCount: number;
  sharesCount: number;
  isLikedByUser: boolean;
  isTrending: boolean;
  heatScore: number;
};

export type UserInteractions = {
  isLikedByUser: boolean;
};

/**
 * Fetches all social metrics for content in one call
 */
export const fetchMetrics = async (contentId: string, contentType: ContentType = 'card'): Promise<SocialMetrics> => {
  try {
    const [commentsCount, likesCount, sharesCount] = await Promise.all([
      commentsMetricsService.getCount(contentId, contentType),
      likesMetricsService.getCount(contentId, contentType),
      sharesMetricsService.getCount(contentId, contentType)
    ]);

    // Calculate heat score (likes + comments*2 + shares*3)
    const heatScore = likesCount + (commentsCount * 2) + (sharesCount * 3);
    
    // Consider trending if heat score is above a threshold
    const isTrending = heatScore > 10;
    
    return {
      commentsCount,
      likesCount,
      sharesCount,
      isLikedByUser: false, // This will be updated by checkUserInteractions
      isTrending,
      heatScore
    };
  } catch (error) {
    console.error('Error fetching social metrics:', error);
    return {
      commentsCount: 0,
      likesCount: 0,
      sharesCount: 0,
      isLikedByUser: false,
      isTrending: false,
      heatScore: 0
    };
  }
};

/**
 * Checks if a user has interacted with content (liked, etc.)
 */
export const checkUserInteractions = async (
  contentId: string, 
  userId: string | null,
  anonymousId: string | null,
  contentType: ContentType = 'card'
): Promise<UserInteractions> => {
  try {
    // Check if user has liked this content
    let isLikedByUser = false;
    
    if (userId) {
      // Check using user ID
      isLikedByUser = await likesMetricsService.checkUserLike(contentId, contentType, userId);
    } else if (anonymousId) {
      // Check using anonymous ID (if implemented)
      // For now we don't track anonymous likes
    }
    
    return {
      isLikedByUser
    };
  } catch (error) {
    console.error('Error checking user interactions:', error);
    return {
      isLikedByUser: false
    };
  }
};

// Remove the React hook version of useSocialMetrics that was causing errors
